import { supabaseClient } from "@/lib/supabase";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";
import { useLocale } from "@/features/i18n/LocaleProvider";
import { fetchFormNameTranslations } from "./overlayFormTranslations";

/**
 * Catalogue columns only. `forms_view.pages` is a correlated json_agg of
 * pages_view + inputs; selecting it for every available form is what timed out
 * WEBAPP-D0. Load nested pages via selectFormById when a single form is opened.
 */
const formListSelect = [
    "author",
    "canonical_form_id",
    "chart_type",
    "copyright_info",
    "created_at",
    "developer_info",
    "form_caption",
    "form_description",
    "form_id",
    "form_instructions",
    "form_name",
    "image_id",
    "importance_level",
    "indications",
    "interpretation",
    "is_licensed",
    "is_published",
    "is_side_specific",
    "limitations",
    "locale",
    "meaningful_change_threshold",
    "medical_specialties",
    "number_of_pages",
    "number_of_questions",
    "orthopaedic_areas",
    "overview_text",
    "prevalence",
    "price",
    "required_input_keys",
    "scoring",
    "scoring_methodology",
    "show_chart",
    "slug",
    "status",
    "subscales",
    "time_to_complete",
].join(",");

/**
 * Public catalogue: published forms only (safe for anon via forms_select_published).
 * Do not use `available_forms` here - that picker view is for authenticated access modes.
 */
export async function selectPublishedForms() {
    const { data, error } = await supabaseClient
        .from("forms_view")
        .select(formListSelect)
        .eq("is_published", true)
        .throwOnError();
    if (error) throw error;
    return data;
}

export const selectPublishedFormsQueryOptions = () =>
    queryOptions({
        queryKey: ["forms", "published"],
        queryFn: () => selectPublishedForms(),
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

/**
 * Loads form definitions the current user may use.
 * IDs come from `available_forms` (modes + effective grants); details from `forms_view`.
 * Do not filter by `is_published` here - that would hide unpublished forms the user is allowed to use.
 *
 * @param _includeUnpublished Deprecated. Kept for call-site compatibility; ignored.
 */
export async function selectForms(_includeUnpublished = false) {
    const { data: available, error: availableError } = await supabaseClient
        .from("available_forms")
        .select("form_id")
        .throwOnError();
    if (availableError) throw availableError;

    const formIds = (available ?? []).map((row) => row.form_id).filter((id): id is string => Boolean(id));
    if (formIds.length === 0) return [];

    const { data, error } = await supabaseClient
        .from("forms_view")
        .select(formListSelect)
        .in("form_id", formIds)
        .throwOnError();
    if (error) throw error;
    return data;
}

export const selectFormsQueryOptions = (_includeUnpublished = false) =>
    queryOptions({
        queryKey: ["forms"],
        queryFn: () => selectForms(),
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

export function useSelectForms(includeUnpublished = false) {
    return useQuery(selectFormsQueryOptions(includeUnpublished));
}

/**
 * Lightweight forms list for selection UIs (template flow, library, quickstart).
 * Uses `available_forms` so pickers respect access modes and effective grants.
 */
export async function selectFormsLite(locale: SupportedLocale = DEFAULT_LOCALE) {
    const { data, error } = await supabaseClient
        .from("available_forms")
        .select("form_id, form_name, slug, is_published, prevalence, orthopaedic_areas, author")
        .throwOnError();
    if (error) throw error;
    const rows = data ?? [];
    if (locale === DEFAULT_LOCALE) return rows;
    const names = await fetchFormNameTranslations(
        rows.map((row) => row.form_id).filter((id): id is string => Boolean(id)),
        locale
    );
    return rows.map((row) => ({
        ...row,
        form_name: (row.form_id && names.get(row.form_id)) || row.form_name,
    }));
}

export const selectFormsLiteQueryOptions = (locale: SupportedLocale = DEFAULT_LOCALE) =>
    queryOptions({
        queryKey: ["forms", "lite", "available", locale],
        queryFn: () => selectFormsLite(locale),
        // Catalogue is invalidated by formBuilderApi `["forms"]`. Do not refetch on
        // every template wizard remount - that re-ran available_forms per step.
        staleTime: 5 * 60_000,
        gcTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

export function useSelectFormsLite() {
    const locale = useLocale();
    return useQuery(selectFormsLiteQueryOptions(locale));
}
